import { z } from 'zod';
import { safeParseJson } from '../parse-json';

/**
 * Flow filter wire schemas — the canonical FilterGroup shape shared by every
 * Flow table surface (Command Center contact/account views, the main-Terra
 * Contact tab, and — after migration — Playhouse).
 *
 * This is the source of truth. `mini-apps/playhouse/api.ts` and
 * `mini-apps/_shared/components/TableFilterBuilder.tsx` carry structurally
 * identical, not-yet-migrated copies; they collapse onto this module in a
 * follow-up PR (deliberately NOT part of the Command Center v0 build).
 *
 * Frontend imports via `@shared/flow/filter`; backend via relative path.
 */

export const FilterConditionSchema = z.object({
  id: z.string(),
  field: z.string(),
  // Operators beginning with `__` are the server's own markers, written after parsing.
  operator: z.string().refine((op) => !op.startsWith('__'), 'Operators beginning with "__" are reserved'),
  value: z.union([z.string(), z.array(z.string()), z.number(), z.null()]),
});
export type FilterCondition = z.infer<typeof FilterConditionSchema>;

export const FilterGroupSchema = z.object({
  id: z.string(),
  logic: z.enum(['AND', 'OR']),
  conditions: z.array(FilterConditionSchema),
  /**
   * Human name for the whole group, rendered as the chip / group-card header
   * in place of the generated condition summary (e.g. "Sculpt 2026 ·
   * Suggested" for a play-scope preset). Display only — it never affects the
   * compiled SQL.
   *
   * OPTIONAL, permanently: every group written before this field existed
   * lacks the key, and both embedders parse the same schema —
   * `FlowWorkingStateSchema` through `parseDbJson` (which degrades the whole
   * row to null on a parse failure, i.e. silently drops a rep's stored
   * filters) and the saved-view bodies in `view.ts`. An unlabeled group
   * keeps rendering its condition summary.
   */
  label: z.string().optional(),
  /**
   * QA round 8 (Playbooks item 3) — `true` for a group the rep created with "+ Filter group":
   * it renders as a bracketed card even when it is the first (or only) group. Display only,
   * exactly like `label`; never affects compiled SQL. Absent on every group saved before this
   * round, which keeps their rendering byte-identical (group 0 flat, groups 1+ boxed).
   */
  boxed: z.boolean().optional(),
});
export type FilterGroup = z.infer<typeof FilterGroupSchema>;

export function parseFilterGroupsParam(raw: string): FilterGroup[] | null {
  const result = safeParseJson(z.array(FilterGroupSchema), raw);
  return result.success ? result.data : null;
}

export const GroupLogicSchema = z.enum(['AND', 'OR']);
export type GroupLogic = z.infer<typeof GroupLogicSchema>;

/**
 * Value types a filterable field can declare (drives operator choices + SQL).
 *
 * `'person'` is a text name column with an indexed SFDC-user-id companion
 * (`FilterFieldDef.idSql`): literal values compare on the name, while the
 * `{me}` token is rewritten server-side into an id equality before SQL
 * generation. See `src/services/flow/query/condition-to-sql.ts`.
 */
export type FilterFieldType = 'text' | 'number' | 'picklist' | 'boolean' | 'date' | 'person';

/**
 * The value token a person filter carries when it means "the acting user".
 * Stored verbatim in saved views so a shared view resolves per viewer; the
 * server pre-pass rewrites it to a `PERSON_ID_EQUALS_OP` condition.
 */
export const ME_TOKEN = '{me}';
